import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PermitSignOffRole, PermitType, PermitTypeCreate, PermitTypeUpdate } from '@cloudsign/shared';
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { Alert, Badge, Button, Card, CheckboxField, EmptyState, LinkButton, PageHeader, Spinner, TableWrap, TextField, Th } from '../../components/ui';
import { ErrorLine, ErrorPanel, fieldErrors } from '../../components/ErrorPanel';
import { useToast } from '../../components/Toast';
import { slugId, typesKey, usePermitTypes } from './api';

type Role = PermitType['issueSignOffs'][number];
type Item = { id: string; text: string; required: boolean };
type Draft = {
  name: string;
  description: string;
  checklist: Item[];
  precautions: string;
  issueSignOffs: PermitType['issueSignOffs'];
  closeSignOffs: PermitType['closeSignOffs'];
  maxHours: number;
  active: boolean;
};

/** Always-on roles: the issuer signs to issue; the issuer and the contractor sign to close (permits.md s2). */
const ISSUE_FIXED: PermitType['issueSignOffs'] = ['issuer'];
const CLOSE_FIXED: PermitType['closeSignOffs'] = ['issuer', 'contractor'];
const withFixed = (roles: PermitType['issueSignOffs'], fixed: PermitType['issueSignOffs']): PermitType['issueSignOffs'] => [...new Set([...fixed, ...roles])];

const BLANK: Draft = { name: '', description: '', checklist: [], precautions: '', issueSignOffs: ISSUE_FIXED, closeSignOffs: CLOSE_FIXED, maxHours: 12, active: true };
const toDraft = (x: PermitType): Draft => ({
  name: x.name,
  description: x.description,
  checklist: x.checklist.map((i) => ({ ...i })),
  precautions: x.precautions,
  issueSignOffs: withFixed(x.issueSignOffs, ISSUE_FIXED),
  closeSignOffs: withFixed(x.closeSignOffs, CLOSE_FIXED),
  maxHours: x.maxHours,
  active: x.active,
});

/** Permit types editor: checklist builder, sign-offs, longest duration (permits.md s5). */
export function PermitTypesPage() {
  const q = usePermitTypes();
  const canManage = useCan('permits.manage_types');
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  return (
    <div className="space-y-4">
      <PageHeader
        title={t('p3pt.types.title')}
        description={t('p3pt.types.description')}
        actions={
          <>
            <LinkButton to="/permits">{t('p3pt.detail.back')}</LinkButton>
            {canManage ? (
              <Button variant="primary" onClick={() => setEditing({ id: null, draft: BLANK })}>
                {t('p3pt.types.add')}
              </Button>
            ) : null}
          </>
        }
      />
      {q.isError ? (
        <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />
      ) : !q.data ? (
        <Spinner />
      ) : q.data.length === 0 ? (
        <EmptyState message={t('p3pt.types.empty')} />
      ) : (
        <TableWrap caption={t('p3pt.types.title')}>
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <Th>{t('p3pt.types.name')}</Th>
              <Th>{t('p3pt.types.maxHours')}</Th>
              <Th>{t('p3pt.types.items')}</Th>
              <Th>{t('p3pt.filter.status')}</Th>
              <Th />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {q.data.map((x) => (
              <tr key={x.id}>
                <td className="px-4 py-2.5 font-medium">
                  {x.name}
                  {x.description ? <span className="block text-xs font-normal text-slate-600">{x.description}</span> : null}
                </td>
                <td className="px-4 py-2.5">{x.maxHours}</td>
                <td className="px-4 py-2.5">{x.checklist.length}</td>
                <td className="px-4 py-2.5">
                  <Badge tone={x.active ? 'good' : 'neutral'}>{x.active ? t('p3pt.types.active') : t('p3pt.types.inactive')}</Badge>
                </td>
                <td className="px-4 py-2.5 text-right">
                  {canManage ? (
                    <Button onClick={() => setEditing({ id: x.id, draft: toDraft(x) })}>
                      {t('common.edit')}
                      <span className="sr-only"> {x.name}</span>
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}
      {editing ? <TypeEditor key={editing.id ?? 'new'} id={editing.id} original={editing.id ? originalOf(q.data, editing.id) : null} draft={editing.draft} onClose={() => setEditing(null)} /> : null}
    </div>
  );
}

const originalOf = (list: PermitType[] | undefined, id: string): Draft | null => {
  const x = list?.find((y) => y.id === id);
  return x ? toDraft(x) : null;
};

function TypeEditor({ id, original, draft: initial, onClose }: { id: string | null; original: Draft | null; draft: Draft; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [d, setD] = useState<Draft>(initial);
  const set = (patch: Partial<Draft>) => setD((cur) => ({ ...cur, ...patch }));
  const save = useMutation({
    mutationFn: () => {
      if (id === null) {
        const body = PermitTypeCreate.parse({ ...d, name: d.name.trim(), checklist: d.checklist.map((i) => ({ ...i, text: i.text.trim() })) });
        return api.post('/permits/types', { body, schema: PermitType });
      }
      // A PATCH sends only what changed, so nothing else is reset.
      const cleaned = { ...d, name: d.name.trim(), checklist: d.checklist.map((i) => ({ ...i, text: i.text.trim() })) };
      const patch: Record<string, unknown> = {};
      for (const k of Object.keys(cleaned) as (keyof Draft)[]) {
        if (JSON.stringify(cleaned[k]) !== JSON.stringify(original?.[k])) patch[k] = cleaned[k];
      }
      return api.patch(`/permits/types/${id}`, { body: PermitTypeUpdate.parse(patch), schema: PermitType });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: typesKey });
      toast(t('p3pt.types.saved'));
      onClose();
    },
  });
  const errs = fieldErrors(save.error);
  const move = (n: number, by: number) => {
    const next = [...d.checklist];
    const [item] = next.splice(n, 1);
    if (!item) return;
    next.splice(n + by, 0, item);
    set({ checklist: next });
  };
  const toggleRole = (key: 'issueSignOffs' | 'closeSignOffs', role: Role, on: boolean) =>
    set({ [key]: on ? [...d[key], role] : d[key].filter((r) => r !== role) } as Partial<Draft>);
  const valid = d.name.trim() !== '' && d.maxHours >= 1 && d.maxHours <= 336 && d.checklist.every((i) => i.text.trim() !== '');
  return (
    <Card
      title={id === null ? t('p3pt.types.add') : t('p3pt.types.edit')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <TextField label={t('p3pt.types.name')} value={d.name} maxLength={80} error={errs['name']} onChange={(e) => set({ name: e.target.value })} />
        <TextField label={t('p3pt.field.description')} value={d.description} maxLength={1000} onChange={(e) => set({ description: e.target.value })} />
        <TextField
          label={t('p3pt.types.maxHours')}
          hint={t('p3pt.types.maxHoursHint')}
          type="number"
          min={1}
          max={336}
          value={String(d.maxHours)}
          error={errs['maxHours']}
          onChange={(e) => set({ maxHours: Number.parseInt(e.target.value, 10) || 0 })}
        />
        <fieldset className="space-y-2">
          <legend className="text-sm font-medium text-slate-800">{t('p3pt.detail.checklist')}</legend>
          {d.checklist.length === 0 ? <p className="text-sm text-slate-600">{t('p3pt.types.noItems')}</p> : null}
          <ul className="space-y-3">
            {d.checklist.map((item, n) => (
              <li key={item.id} className="flex flex-wrap items-end gap-2 rounded-lg border border-slate-200 p-2">
                <TextField
                  className="min-w-[12rem] flex-1"
                  label={t('p3pt.types.itemText', { n: n + 1 })}
                  value={item.text}
                  maxLength={300}
                  onChange={(e) => set({ checklist: d.checklist.map((x, i) => (i === n ? { ...x, text: e.target.value } : x)) })}
                />
                <CheckboxField
                  label={t('p3pt.types.itemRequired')}
                  checked={item.required}
                  onChange={(e) => set({ checklist: d.checklist.map((x, i) => (i === n ? { ...x, required: e.target.checked } : x)) })}
                />
                <Button aria-label={t('p3pt.types.moveUp', { n: n + 1 })} disabled={n === 0} onClick={() => move(n, -1)}>
                  <ArrowUp aria-hidden="true" className="h-4 w-4" />
                </Button>
                <Button aria-label={t('p3pt.types.moveDown', { n: n + 1 })} disabled={n === d.checklist.length - 1} onClick={() => move(n, 1)}>
                  <ArrowDown aria-hidden="true" className="h-4 w-4" />
                </Button>
                <Button aria-label={t('p3pt.types.removeItem', { n: n + 1 })} onClick={() => set({ checklist: d.checklist.filter((_, i) => i !== n) })}>
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                </Button>
              </li>
            ))}
          </ul>
          {d.checklist.length >= 50 ? (
            <Alert kind="info">{t('p3pt.types.itemsMax')}</Alert>
          ) : (
            <Button onClick={() => set({ checklist: [...d.checklist, { id: slugId(`item ${d.checklist.length + 1}`, d.checklist.map((i) => i.id)), text: '', required: true }] })}>{t('p3pt.types.addItem')}</Button>
          )}
        </fieldset>
        <div>
          <label htmlFor="p3pt-type-precautions" className="mb-1 block text-sm font-medium text-slate-800">
            {t('p3pt.field.precautions')}
          </label>
          <textarea id="p3pt-type-precautions" rows={4} maxLength={5000} value={d.precautions} onChange={(e) => set({ precautions: e.target.value })} className="block w-full rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm" />
        </div>
        <RoleBoxes legend={t('p3pt.types.issueSignOffs')} selected={d.issueSignOffs} fixed={ISSUE_FIXED} onToggle={(r, on) => toggleRole('issueSignOffs', r, on)} />
        <RoleBoxes legend={t('p3pt.types.closeSignOffs')} selected={d.closeSignOffs} fixed={CLOSE_FIXED} onToggle={(r, on) => toggleRole('closeSignOffs', r, on)} />
        {id !== null ? <CheckboxField label={t('p3pt.types.activeLabel')} hint={t('p3pt.types.activeHint')} checked={d.active} onChange={(e) => set({ active: e.target.checked })} /> : null}
        <ErrorLine error={save.error} />
      </div>
    </Card>
  );
}

function RoleBoxes({ legend, selected, fixed, onToggle }: { legend: string; selected: readonly string[]; fixed: readonly string[]; onToggle: (role: Role, on: boolean) => void }) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium text-slate-800">{legend}</legend>
      {PermitSignOffRole.options.map((r) => (
        <CheckboxField
          key={r}
          label={t(`p3pt.role.${r}`)}
          checked={fixed.includes(r) || selected.includes(r)}
          disabled={fixed.includes(r)}
          onChange={(e) => onToggle(r, e.target.checked)}
        />
      ))}
    </fieldset>
  );
}
